/**
 * Base tree node interface that all tree nodes must satisfy.
 * This allows findFirstFilePath to work with any tree structure.
 */
export type TreeNodeLike = {
  type: string;
  path: string;
  children?: TreeNodeLike[];
};

/**
 * Recursively finds the first file path in a tree structure.
 * This is useful for auto-selecting the first file when a tree loads.
 *
 * @param {TreeNodeLike[]} nodes - Array of tree nodes to search
 * @returns {string | null} - The path of the first file found, or null if no files exist
 *
 * @example
 * const tree = [
 *   {
 *     type: 'directory',
 *     path: '/src',
 *     children: [
 *       { type: 'file', path: '/src/index.ts' },
 *       { type: 'file', path: '/src/utils.ts' }
 *     ]
 *   }
 * ];
 * findFirstFilePath(tree)
 * // => '/src/index.ts'
 */
export function findFirstFilePath(nodes: TreeNodeLike[]): string | null {
  // Using for...of instead of forEach because it allows early return
  for (const node of nodes) {
    if (node.type === 'file') {
      return node.path;
    }
    if (node.type === 'directory' && node.children) {
      const result = findFirstFilePath(node.children);
      if (result) {
        return result;
      }
    }
  }
  return null;
}
